/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

/**
 * Hardcoded metro lookup. Used to center the map on the agent's most-represented
 * metro when they open the area picker. Lauft's markets are bounded — adding a
 * new MLS board means adding a new entry here. Mirror the pattern used for MLS
 * board codes elsewhere in the app.
 *
 * Coordinates are [latitude, longitude] (Leaflet convention, NOT GeoJSON).
 * Zoom 11 is roughly "inner-metro neighborhood-level" — close enough that
 * drawing a useful polygon doesn't require panning, far enough that the metro
 * fits in view.
 *
 * The `cities` array is matched case-insensitively against the agent's claimed
 * city names. Anchor cities should appear in the list of their own metro so a
 * "Denver" claim alone correctly resolves to Denver Metro.
 */
const METROS = {
    DenverMetro: {
      center: [39.7392, -104.9903],
      zoom: 11,
      cities: [
        'Denver', 'Aurora', 'Lakewood', 'Centennial', 'Englewood',
        'Boulder', 'Arvada', 'Westminster', 'Thornton', 'Littleton',
        'Wheat Ridge', 'Commerce City', 'Northglenn', 'Broomfield',
        'Golden', 'Parker', 'Highlands Ranch', 'Castle Rock', 'Castle Pines',
        'Brighton', 'Lone Tree', 'Cherry Hills Village', 'Greenwood Village',
        'Edgewater', 'Glendale', 'Sheridan', 'Bow Mar', 'Columbine Valley',
        'Erie', 'Lafayette', 'Louisville', 'Superior', 'Niwot'
      ]
    },
    ColoradoSprings: {
      center: [38.8339, -104.8214],
      zoom: 11,
      cities: [
        'Colorado Springs', 'Manitou Springs', 'Fountain', 'Falcon',
        'Monument', 'Black Forest', 'Security-Widefield', 'Cimarron Hills',
        'Stratmoor', 'Woodmoor', 'Gleneagle', 'Palmer Lake'
      ]
    },
    DFW: {
      // Anchored on Dallas; Plano-side agents still get a reasonable default.
      center: [32.7767, -96.7970],
      zoom: 10,
      cities: [
        'Dallas', 'Fort Worth', 'Arlington', 'Plano', 'Frisco', 'Allen',
        'McKinney', 'Garland', 'Irving', 'Grand Prairie', 'Mesquite',
        'Carrollton', 'Richardson', 'Lewisville', 'Flower Mound', 'Denton',
        'Mansfield', 'Euless', 'Bedford', 'Grapevine', 'Coppell', 'Southlake',
        'Colleyville', 'Keller', 'North Richland Hills', 'Hurst', 'Rowlett',
        'Wylie', 'Sachse', 'Murphy', 'Addison', 'Farmers Branch', 'Highland Park',
        'University Park', 'Rockwall', 'Heath', 'Fate', 'Forney', 'Sunnyvale',
        'Lancaster', 'DeSoto', 'Cedar Hill', 'Duncanville', 'Midlothian',
        'Waxahachie', 'Red Oak', 'Ovilla'
      ]
    },
    Austin: {
      center: [30.2672, -97.7431],
      zoom: 10,
      cities: [
        'Austin', 'Round Rock', 'Cedar Park', 'Pflugerville', 'Georgetown',
        'Leander', 'Hutto', 'Buda', 'Kyle', 'San Marcos', 'Manor',
        'Lakeway', 'Bee Cave', 'West Lake Hills', 'Rollingwood', 'Sunset Valley',
        'Liberty Hill', 'Jonestown', 'Lago Vista', 'Wimberley', 'Dripping Springs',
        'Bastrop', 'Elgin', 'Taylor'
      ]
    },
    Houston: {
      center: [29.7604, -95.3698],
      zoom: 10,
      cities: [
        'Houston', 'Katy', 'Sugar Land', 'The Woodlands', 'Pearland',
        'League City', 'Pasadena', 'Missouri City', 'Friendswood', 'Spring',
        'Cypress', 'Tomball', 'Humble', 'Kingwood', 'Bellaire', 'West University Place',
        'Conroe', 'Magnolia', 'Richmond', 'Rosenberg', 'Stafford'
      ]
    },
    EastBay: {
      center: [37.8044, -122.2712],
      zoom: 11,
      cities: [
        'Oakland', 'Berkeley', 'Fremont', 'Hayward', 'Richmond', 'Alameda',
        'San Leandro', 'Union City', 'Pleasanton', 'Livermore', 'Dublin',
        'Walnut Creek', 'Concord', 'Pleasant Hill', 'Lafayette', 'Orinda',
        'Moraga', 'Albany', 'El Cerrito', 'San Pablo', 'Pinole', 'Hercules',
        'Castro Valley', 'San Ramon', 'Danville', 'Alamo', 'Antioch',
        'Brentwood', 'Pittsburg', 'Martinez'
      ]
    },
    Marin: {
      center: [37.9735, -122.5311],
      zoom: 11,
      cities: [
        'San Rafael', 'Novato', 'Mill Valley', 'Sausalito', 'Tiburon',
        'Belvedere', 'Larkspur', 'Corte Madera', 'San Anselmo', 'Fairfax',
        'Ross', 'Kentfield', 'Greenbrae', 'Strawberry'
      ]
    }
  };
  
  // Default fallback when an agent has no claimed territories at all,
  // or none that match a known metro. Picked Denver Metro because it's
  // Lauft's largest market.
  const FALLBACK = { center: [39.7392, -104.9903], zoom: 9, key: 'DenverMetro' };
  
  /**
   * Strip the state and zip from a "City, ST 12345" string. Agents claim
   * cities sometimes as bare names ("Denver") and sometimes with state/zip
   * pulled from the csz field. Normalize to the city portion only.
   */
  function cleanCityName(raw) {
    if (!raw || typeof raw !== 'string') return '';
    // Take everything before the first comma; trim whitespace.
    const beforeComma = raw.split(',')[0];
    return beforeComma.trim();
  }
  
  /**
   * Given a list of the agent's claimed cities, pick the metro with the
   * most matching cities and return its center/zoom for the map.
   *
   * Tie-breaking: alphabetical by metro key, so the same agent always sees
   * the same default.
   *
   * @param {string[]} claimedCities  e.g. ['Aurora', 'Lakewood, CO 80228', 'Plano']
   * @returns {{ center: [number, number], zoom: number, metroKey: string }}
   */
  function pickMapCenter(claimedCities = []) {
    if (!Array.isArray(claimedCities) || claimedCities.length === 0) {
      return { ...FALLBACK, metroKey: FALLBACK.key };
    }
  
    const normalized = claimedCities
      .map(cleanCityName)
      .filter(Boolean)
      .map(c => c.toLowerCase());
  
    if (normalized.length === 0) {
      return { ...FALLBACK, metroKey: FALLBACK.key };
    }
  
    // Count matches per metro.
    const counts = {};
    for (const [key, metro] of Object.entries(METROS)) {
      const cityLower = metro.cities.map(c => c.toLowerCase());
      const matches = normalized.filter(c => cityLower.includes(c)).length;
      if (matches > 0) counts[key] = matches;
    }
  
    const ranked = Object.entries(counts).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];   // most matches wins
      return a[0].localeCompare(b[0]);          // tie → alphabetical
    });
  
    if (ranked.length === 0) {
      // Agent has territories but none are in a known metro. Use the fallback.
      return { ...FALLBACK, metroKey: FALLBACK.key };
    }
  
    const winner = ranked[0][0];
    const m = METROS[winner];
    return { center: m.center, zoom: m.zoom, metroKey: winner };
  }
  
  export { METROS, pickMapCenter, FALLBACK };